"use client"

import type { VariantProps } from "class-variance-authority"
import type { ReactNode } from "react"
import React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import * as SwitchPrimitive from "@radix-ui/react-switch"
import { cva } from "class-variance-authority"
import clsx from "clsx"
import { AnimatePresence } from "motion/react"

import { cn } from "@productlane/lib/src/browser"

const switchVariants = cva(
	"relative flex h-4 flex-none items-center rounded-full bg-gray-300 p-0.5 transition-colors duration-100 focus:outline-none focus-visible:ring-4  dark:bg-gray-600 ",
	{
		variants: {
			// We cant use color because SwitchPrimitive.Root already uses it
			variant: {
				default:
					"data-[state=checked]:bg-green-500 dark:data-[state=checked]:bg-green-500",
				branded:
					"data-[state=checked]:bg-green-500 dark:data-[state=checked]:bg-green-500",
				important:
					"data-[state=checked]:bg-red-500 dark:data-[state=checked]:bg-red-500",
			},
			size: {
				default: "w-7",
				small: "w-6",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

export interface SwitchProps
	extends SwitchPrimitive.SwitchProps, VariantProps<typeof switchVariants> {
	name: string
	label?: ReactNode
	errorMessage?: ReactNode
	description?: ReactNode
	direction?: "switch-first" | "text-first"
	size?: "default" | "small"
	switchClassName?: string
	labelClassName?: string
}

export const Switch = React.memo(
	React.forwardRef<HTMLDivElement, SwitchProps>(
		(
			{
				className,
				label,
				variant,
				size = "default",
				errorMessage,
				value,
				description,
				disabled,
				name,
				direction = "text-first",
				switchClassName,
				labelClassName,
				...props
			},
			ref,
		) => {
			return (
				<div ref={ref} className={cn(className)}>
					<div
						className={cn(
							"relative flex items-center gap-2",
							direction === "switch-first" ? "flex-row" : "flex-row-reverse",
							disabled && "opacity-50",
							switchClassName,
						)}
					>
						<AnimatePresence>
							<SwitchPrimitive.Root
								disabled={disabled}
								value={value}
								{...props}
								id={`switch-${name}`}
								className={cn(switchVariants({ variant, size }))}
							>
								<SwitchPrimitive.Thumb
									className={cn(
										"h-3 w-3 rounded-full bg-white shadow-sm transition-all duration-100 data-[state=checked]:bg-brand-100 dark:bg-brand-900 dark:data-[state=checked]:bg-gray-50",
										size === "small"
											? "data-[state=checked]:translate-x-2"
											: "data-[state=checked]:translate-x-3",
									)}
								/>
							</SwitchPrimitive.Root>
						</AnimatePresence>
						{label && (
							<LabelPrimitive.Root
								htmlFor={`switch-${name}`}
								className={cn(
									"block flex-1 text-sm font-medium text-primary transition-all duration-200",
									labelClassName,
								)}
							>
								{label}
								{description && (
									<div className="mt-0.5 text-left text-xs font-normal text-secondary">
										{description}
									</div>
								)}
							</LabelPrimitive.Root>
						)}
					</div>

					{errorMessage && (
						<div
							className={clsx(
								"mt-1.5 text-sm",
								errorMessage ? "text-red-500" : "text-secondary",
							)}
							id={`${name}-error`}
						>
							{errorMessage}
						</div>
					)}
				</div>
			)
		},
	),
)
